import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { CalendarDays, Clock, Handshake, User, Gamepad2, MapPin, ArrowRight, ArrowLeftRight, RotateCcw, Headphones, MicOff, Mic, Loader2, CheckCircle2, XCircle, Lock, AtSign } from 'lucide-react';
import { RARITIES, VARIANTS, SPRITES } from '../mock';
import SpritePicker from './SpritePicker';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { useTradeRecipient } from '../hooks/useTradeRecipient';

function formatDate(d) {
  if (!d) return '';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

const PLATFORMS = ['PC', 'PlayStation', 'Xbox', 'Nintendo Switch', 'Mobile'];
const MODES = ['Battle Royale', 'Zero Build', 'Creative', 'Party Royale', 'Reload'];

export default function TradeDialog({ open, onOpenChange, sprite, onSubmit }) {
  const { isAuthed, openAuth } = useAuth();
  const { recipient: preselectedRecipient } = useTradeRecipient();
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [svUsername, setSvUsername] = useState('');
  const [epicId, setEpicId] = useState('');
  const [lookup, setLookup] = useState({ status: 'idle', user: null, error: '' });
  const [platform, setPlatform] = useState('PC');
  const [mode, setMode] = useState('Battle Royale');
  const [location, setLocation] = useState('');
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState('18:00');
  const [note, setNote] = useState('');
  const [offerSpriteId, setOfferSpriteId] = useState(null);
  const [tradeType, setTradeType] = useState('swap');
  const [hasMic, setHasMic] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setSvUsername(''); setEpicId(''); setLookup({ status: 'idle', user: null, error: '' });
    setPlatform('PC'); setMode('Battle Royale');
    setLocation(''); setDate(undefined); setTime('18:00'); setNote('');
    setOfferSpriteId(null);
    setTradeType('swap');
    setHasMic(true);
    setSubmitting(false);
  };

  // Pre-fill username from the global "selected recipient" each time the
  // dialog opens (set via the UserSearchBar above the sprite grid).
  useEffect(() => {
    if (open && preselectedRecipient?.username && !svUsername) {
      setSvUsername(preselectedRecipient.username);
    }
  }, [open, preselectedRecipient, svUsername]);

  // Debounced live username lookup
  useEffect(() => {
    if (!isAuthed) return;
    const cleaned = svUsername.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (!cleaned) {
      setLookup({ status: 'idle', user: null, error: '' });
      return;
    }
    setLookup((p) => ({ ...p, status: 'searching' }));
    const t = setTimeout(async () => {
      try {
        const u = await api.lookupUser(cleaned);
        setLookup({ status: 'found', user: u, error: '' });
      } catch (e) {
        setLookup({
          status: e.status === 404 ? 'notfound' : 'error',
          user: null,
          error: e.message || 'Lookup failed',
        });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [svUsername, isAuthed]);

  if (!sprite) return null;
  // The sprite the user CLICKED is what they WANT
  const wantSprite = sprite;
  const wantRarity = RARITIES[wantSprite.rarity];
  const wantVariant = VARIANTS[wantSprite.variant];

  const handleSubmit = async () => {
    if (!isAuthed) {
      toast({ title: 'Sign in required', description: 'Sign in to send a trade request.' });
      return;
    }
    if (lookup.status !== 'found' || !lookup.user) {
      toast({ title: 'Recipient required', description: 'Enter a valid Sprite Vault username.' });
      return;
    }
    if (tradeType === 'swap' && !offerSpriteId) {
      toast({ title: 'Pick a sprite to offer', description: 'Choose which sprite you will send in return, or switch to Borrow mode.' });
      return;
    }
    if (!date) {
      toast({ title: 'Pick a date', description: 'Choose a date for the in-game meet-up.' });
      return;
    }
    const isoMeet = (() => {
      const [h, m] = time.split(':').map(Number);
      const d = new Date(date);
      d.setHours(h || 0, m || 0, 0, 0);
      return d.toISOString();
    })();
    const offer = tradeType === 'swap' ? SPRITES.find((s) => s.id === offerSpriteId) : null;

    setSubmitting(true);
    try {
      const created = await api.createTradeReq({
        recipient_username: lookup.user.username,
        trade_type: tradeType,
        has_mic: hasMic,
        want_id: wantSprite.id,
        want_name: wantSprite.name,
        want_img: wantSprite.img,
        want_rarity: wantSprite.rarity,
        want_variant: wantSprite.variant,
        offer_id: offer ? offer.id : null,
        offer_name: offer ? offer.name : null,
        offer_img: offer ? offer.img : null,
        offer_rarity: offer ? offer.rarity : null,
        offer_variant: offer ? offer.variant : null,
        epic_id: epicId.trim(),
        platform,
        mode,
        location: location.trim(),
        meet_at: isoMeet,
        note: note.trim(),
      });

      toast({
        title: 'Trade request sent!',
        description: `${lookup.user.name || lookup.user.username} will get a notification.`,
      });
      // Notify parent (keeps existing local "outgoing trades" cache so the
      // sender still sees something in their TradesSheet on legacy clients)
      onSubmit?.({
        backendId: created.id,
        tradeType, hasMic,
        spriteId: offer ? offer.id : null,
        spriteName: offer ? offer.name : null,
        spriteImg: offer ? offer.img : null,
        spriteRarity: offer ? offer.rarity : null,
        spriteVariant: offer ? offer.variant : null,
        wantId: wantSprite.id, wantName: wantSprite.name, wantImg: wantSprite.img,
        wantRarity: wantSprite.rarity, wantVariant: wantSprite.variant,
        recipient: lookup.user.username,
        recipientName: lookup.user.name,
        epicId: epicId.trim(),
        platform, mode,
        location: location.trim(),
        meetAt: isoMeet,
        note: note.trim(),
      });
      reset();
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Could not send trade', description: e.message || 'Try again.' });
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-[520px] w-[calc(100vw-16px)] max-h-[92vh] border-[3px] border-[#3a1064] rounded-2xl shadow-[0_8px_0_#3a1064] p-0 overflow-y-auto">
        {/* Header banner */}
        <div
          className="p-5 border-b-[3px] border-[#3a1064]"
          style={{ background: `linear-gradient(135deg, ${wantVariant.color}33, ${wantRarity.color}66)` }}
        >
          <DialogHeader className="text-left space-y-1">
            <DialogTitle className="text-[#3a1064] font-black text-2xl flex items-center gap-2" data-testid="trade-dialog-title">
              <Handshake className="h-6 w-6 text-[#ff2bd6]" />
              {tradeType === 'borrow' ? 'Borrow a Sprite' : 'Propose a 1-for-1 Trade'}
            </DialogTitle>
            <DialogDescription className="text-[#3a1064]/80 font-bold">
              {tradeType === 'borrow'
                ? 'Ask to borrow this sprite — return it next match.'
                : 'Pick a sprite to send in return for the one you want.'}
            </DialogDescription>
          </DialogHeader>

          {/* Swap visual: You offer (picker) -> You want (clicked sprite) */}
          <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-stretch gap-2">
            {/* Offer (picked) — or Borrow note */}
            <div className="bg-white rounded-xl p-2.5 border-2 border-[#3a1064] flex items-center justify-center">
              {tradeType === 'borrow' ? (
                <div className="flex items-center gap-2 w-full">
                  <div className="h-12 w-12 rounded-lg bg-[#ffd900] border-2 border-[#3a1064] flex items-center justify-center shrink-0">
                    <RotateCcw className="h-6 w-6 text-[#3a1064]" strokeWidth={3} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[9px] font-black uppercase tracking-wider text-[#3a1064]/70">You promise</div>
                    <div className="font-black text-[#3a1064] text-sm leading-tight">Borrow & return</div>
                    <div className="text-[10px] font-bold text-[#3a1064]/70 mt-0.5">next match</div>
                  </div>
                </div>
              ) : (() => {
                const offer = SPRITES.find((s) => s.id === offerSpriteId);
                if (!offer) {
                  return (
                    <div className="text-center w-full">
                      <div className="text-[9px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-0.5">You offer</div>
                      <div className="text-xs font-bold text-[#3a1064]/60">Pick a sprite below ↓</div>
                    </div>
                  );
                }
                const orr = RARITIES[offer.rarity];
                const ovv = VARIANTS[offer.variant];
                return (
                  <div className="flex items-center gap-2 w-full">
                    <img src={offer.img} alt={offer.name} className="h-12 w-12 object-contain shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[9px] font-black uppercase tracking-wider text-[#3a1064]/70">You offer</div>
                      <div className="font-black text-[#3a1064] text-sm truncate">{offer.name}</div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-[9px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: orr.color }}>{orr.label}</span>
                        <span className="text-[9px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: ovv.color }}>{ovv.label}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            {/* Arrow */}
            <div className="flex items-center justify-center px-1">
              <div className="h-10 w-10 rounded-full bg-[#ffd900] border-2 border-[#3a1064] flex items-center justify-center shadow-[0_2px_0_#3a1064]">
                <ArrowRight className="h-5 w-5 text-[#3a1064]" strokeWidth={3} />
              </div>
            </div>
            {/* Want (clicked sprite, fixed) */}
            <div className="bg-white rounded-xl p-2.5 border-2 border-[#3a1064] flex items-center gap-2">
              <img src={wantSprite.img} alt={wantSprite.name} className="h-12 w-12 object-contain shrink-0" />
              <div className="min-w-0">
                <div className="text-[9px] font-black uppercase tracking-wider text-[#3a1064]/70">You want</div>
                <div className="font-black text-[#3a1064] text-sm truncate">{wantSprite.name}</div>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-[9px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: wantRarity.color }}>{wantRarity.label}</span>
                  <span className="text-[9px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: wantVariant.color }}>{wantVariant.label}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Trade type toggle */}
          <div>
            <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5">
              <ArrowLeftRight className="h-4 w-4" /> Trade type
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="trade-type-swap-btn"
                onClick={() => setTradeType('swap')}
                className={`h-11 rounded-xl border-2 border-[#3a1064] font-black text-sm flex items-center justify-center gap-2 transition-all ${
                  tradeType === 'swap'
                    ? 'bg-[#ff2bd6] text-white shadow-[0_3px_0_#3a1064]'
                    : 'bg-white text-[#3a1064] hover:bg-yellow-50'
                }`}
              >
                <ArrowLeftRight className="h-4 w-4" /> Swap 1-for-1
              </button>
              <button
                type="button"
                data-testid="trade-type-borrow-btn"
                onClick={() => setTradeType('borrow')}
                className={`h-11 rounded-xl border-2 border-[#3a1064] font-black text-sm flex items-center justify-center gap-2 transition-all ${
                  tradeType === 'borrow'
                    ? 'bg-[#9d3dff] text-white shadow-[0_3px_0_#3a1064]'
                    : 'bg-white text-[#3a1064] hover:bg-yellow-50'
                }`}
              >
                <RotateCcw className="h-4 w-4" /> Borrow & Return
              </button>
            </div>
            {tradeType === 'borrow' && (
              <div className="mt-2 text-xs font-bold text-[#3a1064]/80 bg-yellow-100 border-2 border-[#ffd900] rounded-lg px-3 py-2 flex items-start gap-2">
                <RotateCcw className="h-4 w-4 mt-0.5 shrink-0 text-[#9d3dff]" />
                <span>Trust-based borrow — promise to return it to them next match.</span>
              </div>
            )}
          </div>

          {/* Mic toggle */}
          <div>
            <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5">
              <Headphones className="h-4 w-4" /> Comms
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="trade-mic-yes-btn"
                onClick={() => setHasMic(true)}
                className={`h-11 rounded-xl border-2 border-[#3a1064] font-black text-sm flex items-center justify-center gap-2 transition-all ${
                  hasMic
                    ? 'bg-emerald-500 text-white shadow-[0_3px_0_#3a1064]'
                    : 'bg-white text-[#3a1064] hover:bg-yellow-50'
                }`}
              >
                <Headphones className="h-4 w-4" /> Has Mic
              </button>
              <button
                type="button"
                data-testid="trade-mic-no-btn"
                onClick={() => setHasMic(false)}
                className={`h-11 rounded-xl border-2 border-[#3a1064] font-black text-sm flex items-center justify-center gap-2 transition-all ${
                  !hasMic
                    ? 'bg-rose-500 text-white shadow-[0_3px_0_#3a1064]'
                    : 'bg-white text-[#3a1064] hover:bg-yellow-50'
                }`}
              >
                <MicOff className="h-4 w-4" /> No Mic
              </button>
            </div>
          </div>

          {tradeType === 'swap' && (
            <div>
              <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5">
                <Handshake className="h-4 w-4" /> Sprite you&apos;ll send in return
              </Label>
              <SpritePicker
                value={offerSpriteId}
                onChange={setOfferSpriteId}
                excludeId={wantSprite.id}
                label="Pick a sprite to offer..."
              />
            </div>
          )}

          {/* Recipient — Sprite Vault user lookup */}
          <div>
            <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5">
              <AtSign className="h-4 w-4" /> Recipient Sprite Vault username
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3a1064]/60 font-black pointer-events-none">@</span>
              <Input
                data-testid="trade-sv-username-input"
                placeholder="e.g. lootllamafan"
                value={svUsername}
                onChange={(e) => setSvUsername(e.target.value)}
                disabled={!isAuthed}
                className="h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064] pl-8 pr-10"
                autoComplete="off"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2">
                {lookup.status === 'searching' && <Loader2 className="h-4 w-4 animate-spin text-[#3a1064]/60" />}
                {lookup.status === 'found' && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                {lookup.status === 'notfound' && <XCircle className="h-5 w-5 text-rose-500" />}
              </span>
            </div>
            {lookup.status === 'found' && lookup.user && (
              <div
                data-testid="trade-recipient-found"
                className="mt-1.5 flex items-center gap-2 bg-emerald-50 border-2 border-emerald-300 rounded-lg px-2.5 py-1.5 text-xs font-black text-[#3a1064]"
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                Sending to <span className="text-emerald-700">{lookup.user.name || lookup.user.username}</span>
                <span className="text-[#3a1064]/60 font-bold">(@{lookup.user.username})</span>
              </div>
            )}
            {lookup.status === 'notfound' && svUsername.trim() && (
              <div className="mt-1.5 text-xs font-bold text-rose-700">
                No Sprite Vault user with that handle. Double-check spelling.
              </div>
            )}
            <p className="text-[10px] font-bold text-[#3a1064]/60 mt-1">
              The recipient will get an in-app notification and can accept or decline.
            </p>
          </div>

          {/* Optional Epic ID for in-game meet-up context */}
          <div>
            <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5">
              <User className="h-4 w-4" /> Epic ID <span className="text-[#3a1064]/50 font-bold ml-1">(useful for in-game meet-up)</span>
            </Label>
            <Input
              data-testid="trade-epic-id-input"
              placeholder="e.g. NinjaSlayer42"
              value={epicId}
              onChange={(e) => setEpicId(e.target.value)}
              className="h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5">
                <Gamepad2 className="h-4 w-4" /> Platform
              </Label>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064] bg-white px-3"
              >
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5">
                Mode
              </Label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                className="w-full h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064] bg-white px-3"
              >
                {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5">
              <MapPin className="h-4 w-4" /> Meet-up spot (optional)
            </Label>
            <Input
              placeholder="e.g. Tilted Towers, Lobby, Creative island code"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5">
                <CalendarDays className="h-4 w-4" /> Date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064] justify-start"
                  >
                    <CalendarDays className="h-4 w-4 mr-2" />
                    {date ? formatDate(date) : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 border-2 border-[#3a1064] rounded-xl" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < today}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5">
                <Clock className="h-4 w-4" /> Time
              </Label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064] bg-white px-3"
              />
            </div>
          </div>

          <div>
            <Label className="text-[#3a1064] font-black mb-1.5 block">Message (optional)</Label>
            <Textarea
              placeholder="Anything they should know about the trade..."
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="rounded-xl border-2 border-[#3a1064] font-medium text-[#3a1064]"
            />
          </div>
        </div>

        <DialogFooter className="p-5 pt-0">
          <Button
            variant="outline"
            className="h-11 rounded-xl border-2 border-[#3a1064] text-[#3a1064] font-black"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            data-testid="trade-submit-btn"
            className="h-11 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064]"
            onClick={handleSubmit}
          >
            <Handshake className="h-4 w-4 mr-2" />
            {tradeType === 'borrow' ? 'Send Borrow Request' : 'Send Trade Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
